import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  Divider,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Lock as LockIcon,
  CheckCircle as CheckCircleIcon,
  LocalOffer as PromoIcon,
} from '@mui/icons-material';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { useNavigate } from 'react-router-dom';
import { billingApi } from '../../services/api/billing';
import { 
  validateAddress, 
  normalizeAddress, 
  SUPPORTED_COUNTRIES,
  getStatesForCountry,
  type Address 
} from '../../utils/addressValidation';
import { API_CONFIG } from '../../config/apiConfig';

// Initialize Stripe with publishable key from environment
const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '';

if (!publishableKey || !publishableKey.startsWith('pk_')) {
  console.error('Invalid Stripe publishable key. Make sure VITE_STRIPE_PUBLISHABLE_KEY is set in .env');
}

// Load Stripe with explicit options for better debugging
const stripePromise = publishableKey ? loadStripe(publishableKey, {
  locale: 'en'
}) : null;

// Pricing configuration
const PRICING = {
  starter: {
    monthly: 29,
    annual: 26.10 // 10% discount
  },
  professional: {
    monthly: 59,
    annual: 53.10 // 10% discount
  },
  enterprise: {
    monthly: 89,
    annual: 80.10 // 10% discount
  },
  industry_specific: {
    monthly: 129,
    annual: 116.10 // 10% discount
  }
};

// Features configuration matching subscription tiers
const PLAN_FEATURES = {
  starter: [
    '5 custom AI teammates',
    'Basic knowledge graph',
    'Up to 5 team members',
    '10GB storage',
    'Email support'
  ],
  professional: [
    '10 custom AI teammates',
    'Advanced knowledge graph',
    'Unlimited team members',
    '100GB storage',
    'Priority support',
    'API access'
  ],
  enterprise: [
    'All Professional features',
    'Unlimited custom AI teammates',
    'Enterprise SSO',
    'Unlimited storage',
    'Dedicated support',
    'Custom integrations',
    'SLA guarantee'
  ],
  industry_specific: [
    'All Professional features',
    'HVAC/Electrical calculations',
    '5 MEP AI teammates',
    'CAD processing',
    'ASHRAE & NEC tools',
    'Climate data',
    'Professional validation',
    'Engineering support'
  ]
};

interface BillingFormProps {
  organizationName: string;
  organizationId: string;
  subscriptionTier: string;
  billingFrequency: string;
  selectedToolkit: string | null;
  onSuccess: (customerId: string, subscriptionId: string) => void;
  onError: (error: string) => void;
}

// Payment form component - this must be inside Elements provider
const PaymentForm: React.FC<{
  billingDetails: any;
  clientSecret: string;
  onSuccess: (customerId: string, subscriptionId: string) => void;
  onError: (error: string) => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
}> = ({ billingDetails, clientSecret, onSuccess, onError, loading, setLoading }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  
  React.useEffect(() => {
    console.log('PaymentForm mounted');
    console.log('Stripe loaded:', !!stripe);
    console.log('Elements loaded:', !!elements);
    console.log('Client Secret in PaymentForm:', clientSecret);
    console.log('Stripe instance:', stripe);
    console.log('Elements instance:', elements);
    
    if (stripe && elements) {
      // Check if payment element is ready
      setTimeout(() => {
        const paymentElement = elements.getElement(PaymentElement);
        console.log('Payment Element after delay:', paymentElement);
        
        // Try to get all elements to debug
        if (!paymentElement) {
          console.error('PaymentElement not found! This might be due to:');
          console.error('1. Invalid client secret format');
          console.error('2. Stripe not properly initialized');
          console.error('3. Elements provider not configured correctly');
          console.error('Client secret format check:', {
            hasCorrectPrefix: clientSecret.startsWith('seti_'),
            hasSuffix: clientSecret.includes('_secret_'),
            length: clientSecret.length
          });
        }
      }, 2000);
    }
  }, [stripe, elements, clientSecret]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    // Prevent duplicate submissions
    if (isSubmitting || hasSubmitted) {
      console.log('Form already submitted, preventing duplicate submission');
      return;
    }

    if (!stripe || !elements) {
      return;
    }

    setIsSubmitting(true);
    setLoading(true);

    try {
      // Submit the Payment Element to get the payment method
      const { error: submitError } = await elements.submit();
      if (submitError) {
        throw new Error(submitError.message);
      }

      // Confirm the setup intent with the payment method
      const { error: confirmError } = await stripe.confirmSetup({
        elements,
        clientSecret,
        confirmParams: {
          return_url: `${window.location.origin}/billing/setup/complete`,
        },
        redirect: 'if_required',
      });

      if (confirmError) {
        // Check if the error is because the SetupIntent is already confirmed
        if (confirmError.code === 'setup_intent_unexpected_state' && 
            confirmError.message?.includes('already succeeded')) {
          console.log('SetupIntent already confirmed, treating as success');
          setHasSubmitted(true);
          onSuccess('', '');
        } else {
          throw new Error(confirmError.message);
        }
      } else {
        // Payment method attached successfully
        // The subscription was already created by the /billing/setup endpoint
        // Just signal success
        setHasSubmitted(true);
        onSuccess('', '');
      }
    } catch (error: any) {
      onError(error.message);
      setIsSubmitting(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Grid container spacing={3}>
        {/* Show billing address */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Billing Address
          </Typography>
          <Paper variant="outlined" style={{ padding: 16, marginBottom: 16, backgroundColor: '#f5f5f5' }}>
            <Typography variant="body2">
              {billingDetails.name}<br />
              {billingDetails.address.line1}<br />
              {billingDetails.address.line2 && <>{billingDetails.address.line2}<br /></>}
              {billingDetails.address.city}, {billingDetails.address.state} {billingDetails.address.postal_code}<br />
              {billingDetails.address.country}
            </Typography>
          </Paper>
        </Grid>

        {/* Payment Element */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Payment Method
          </Typography>
          <Paper variant="outlined" style={{ padding: 24, minHeight: 200 }}>
            {!stripe || !elements ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 150 }}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary" style={{ marginLeft: 16 }}>
                  Loading payment form...
                </Typography>
              </div>
            ) : (
              <>
                <PaymentElement 
                  options={{
                    layout: 'tabs',
                    defaultValues: {
                      billingDetails: billingDetails
                    }
                  }}
                  onReady={() => {
                    console.log('PaymentElement is ready');
                    setLoading(false);
                  }}
                  onChange={(event) => {
                    console.log('PaymentElement changed:', event);
                  }}
                  onLoadError={(error) => {
                    console.error('PaymentElement load error:', error);
                    onError('Failed to load payment form. Please refresh the page.');
                  }}
                />
                {/* Add a fallback message if PaymentElement doesn't render */}
                <Typography variant="caption" color="text.secondary" style={{ marginTop: 8, display: 'block' }}>
                  If the payment form doesn't appear, please refresh the page.
                </Typography>
              </>
            )}
          </Paper>
        </Grid>

        {/* Submit Button */}
        <Grid item xs={12}>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading || !stripe || isSubmitting || hasSubmitted}
            startIcon={loading || isSubmitting ? <CircularProgress size={20} /> : <LockIcon />}
          >
            {isSubmitting || loading ? 'Processing...' : hasSubmitted ? 'Setup Complete' : 'Start Free Trial'}
          </Button>
        </Grid>

        {/* Security Notice */}
        <Grid item xs={12}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <SecurityIcon fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary">
              Your payment information is encrypted and secure. You won't be charged during your free trial.
            </Typography>
          </div>
        </Grid>
      </Grid>
    </form>
  );
};

const BillingForm: React.FC<BillingFormProps> = ({ 
  organizationName, 
  organizationId,
  subscriptionTier,
  billingFrequency,
  selectedToolkit,
  onSuccess, 
  onError 
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [setupComplete, setSetupComplete] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [showPromoField, setShowPromoField] = useState(false);
  const [promoValid, setPromoValid] = useState<boolean | null>(null);
  const [billingDetails, setBillingDetails] = useState({
    name: '',
    email: '',
    address: {
      line1: '',
      line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'US',
    },
  });
  const [addressErrors, setAddressErrors] = useState<Record<string, string>>({});
  const [addressWarnings, setAddressWarnings] = useState<string[]>([]);

  // Validate address when form fields change
  const validateAddressField = (field: keyof Address, value: string) => {
    const updatedAddress = {
      ...billingDetails.address,
      [field]: value
    };
    
    const validation = validateAddress(updatedAddress);
    setAddressErrors(validation.errors);
    setAddressWarnings(validation.warnings || []);
  };

  const handleInitialSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!billingDetails.name || !billingDetails.email) {
      onError('Please fill in all required fields');
      return;
    }

    // Validate address
    const validation = validateAddress(billingDetails.address);
    if (!validation.isValid) {
      setAddressErrors(validation.errors);
      onError('Please correct the address errors');
      return;
    }

    setLoading(true);

    try {
      // Normalize address before sending
      const normalizedBillingDetails = {
        ...billingDetails,
        address: normalizeAddress(billingDetails.address)
      };

      // Check if token exists and is valid
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      
      if (!token) {
        console.log('[BillingSetup] No token found, redirecting to login');
        navigate('/login');
        return;
      }
      
      // Check token expiration
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const now = Date.now() / 1000;
        if (payload.exp && payload.exp < now) {
          console.log('[BillingSetup] Token expired, redirecting to login');
          localStorage.removeItem('authToken');
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          navigate('/login');
          return;
        }
      } catch (e) {
        console.error('[BillingSetup] Error parsing token:', e);
      }
      
      console.log('Token from localStorage:', token ? `${token.substring(0, 20)}...` : 'No token found');
      
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      };
      console.log('[BillingSetup] Request headers:', headers);
      console.log('[BillingSetup] Request payload:', {
        organizationName,
        organizationId,
        subscriptionTier,
        billingFrequency,
        selectedToolkit: selectedToolkit, // Now sending null instead of undefined
        hasSelectedToolkit: selectedToolkit !== null && selectedToolkit !== undefined,
        selectedToolkitValue: selectedToolkit || 'NULL',
        promoCode: promoCode || undefined,
        billingDetails: normalizedBillingDetails,
      });
      
      const response = await fetch(`${API_CONFIG.BILLING_SERVICE_URL}/api/v1/billing/setup`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          organizationName,
          organizationId,
          subscriptionTier,
          billingFrequency,
          selectedToolkit: selectedToolkit, // Send null if no toolkit selected
          promoCode: promoCode || undefined,
          billingDetails: normalizedBillingDetails,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('[BillingSetup] Billing setup failed:', {
          status: response.status,
          statusText: response.statusText,
          error: error,
          requestBody: {
            organizationName,
            organizationId,
            subscriptionTier,
            billingFrequency,
            selectedToolkit: selectedToolkit, // Now sending null instead of undefined
            promoCode: promoCode || undefined,
            billingDetails: normalizedBillingDetails,
          }
        });
        
        // Handle 401 Unauthorized specifically
        if (response.status === 401) {
          console.log('[BillingSetup] Received 401, token expired, redirecting to login');
          localStorage.removeItem('authToken');
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          navigate('/login');
          return;
        }
        
        let errorMessage = 'Failed to setup billing';
        if (error.error?.message) {
          errorMessage = error.error.message;
        } else if (error.message) {
          errorMessage = error.message;
        } else if (error.error && typeof error.error === 'string') {
          errorMessage = error.error;
        }
        
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('Billing setup response:', result);
      
      if (!result.data) {
        throw new Error('Invalid response from billing setup');
      }
      
      // Check if setup is already complete
      if (result.data.alreadySetup) {
        console.log('Payment method already configured');
        onSuccess('', '');
        return;
      }
      
      if (!result.data.clientSecret) {
        throw new Error('Invalid response from billing setup - missing client secret');
      }
      
      const { clientSecret: cs, customerId } = result.data;
      console.log('Extracted client secret:', cs);
      console.log('Customer ID:', customerId);
      
      setClientSecret(cs);
      setSetupComplete(true);
    } catch (error: any) {
      onError(error.message);
      setLoading(false);
    }
  };


  const validatePromoCode = async () => {
    if (!promoCode) return;

    try {
      const result = await billingApi.validatePromoCode(
        promoCode,
        subscriptionTier as any,
        billingFrequency as any
      );

      setPromoValid(result.valid);
      
      // Show discount information if valid
      if (result.valid && result.discount) {
        console.log('Promo code discount:', result.discount);
      }
    } catch (error) {
      console.error('Error validating promo code:', error);
      setPromoValid(false);
    }
  };

  // If we have a client secret, show the payment form wrapped in Elements
  if (setupComplete && clientSecret) {
    console.log('Client Secret received:', clientSecret);
    console.log('Client Secret length:', clientSecret.length);
    console.log('Client Secret starts with seti_:', clientSecret.startsWith('seti_'));
    console.log('Stripe Promise:', stripePromise);
    console.log('Publishable key being used:', publishableKey);
    
    if (!stripePromise) {
      console.error('Stripe Promise is null! Cannot render payment form.');
      onError('Payment system initialization failed. Please refresh the page.');
      return null;
    }
    
    const stripeOptions = {
      clientSecret,
      appearance: {
        theme: 'stripe' as const,
        variables: {
          colorPrimary: '#325BA8',
        },
      },
    };

    return (
      <Elements stripe={stripePromise} options={stripeOptions} key={clientSecret}>
        <PaymentForm
          billingDetails={billingDetails}
          clientSecret={clientSecret}
          onSuccess={onSuccess}
          onError={onError}
          loading={loading}
          setLoading={setLoading}
        />
      </Elements>
    );
  }

  // Initial form to collect billing details
  return (
    <form onSubmit={handleInitialSubmit}>
      <Grid container spacing={3}>
        {/* Billing Information */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Billing Information
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            required
            label="Full Name"
            value={billingDetails.name}
            onChange={(e) => setBillingDetails({ ...billingDetails, name: e.target.value })}
            disabled={loading}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            required
            type="email"
            label="Email"
            value={billingDetails.email}
            onChange={(e) => setBillingDetails({ ...billingDetails, email: e.target.value })}
            disabled={loading}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            label="Address Line 1"
            value={billingDetails.address.line1}
            onChange={(e) => {
              setBillingDetails({
                ...billingDetails,
                address: { ...billingDetails.address, line1: e.target.value }
              });
              validateAddressField('line1', e.target.value);
            }}
            error={!!addressErrors.line1}
            helperText={addressErrors.line1}
            disabled={loading}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Address Line 2 (Optional)"
            value={billingDetails.address.line2}
            onChange={(e) => {
              setBillingDetails({
                ...billingDetails,
                address: { ...billingDetails.address, line2: e.target.value }
              });
              validateAddressField('line2', e.target.value);
            }}
            error={!!addressErrors.line2}
            helperText={addressErrors.line2}
            disabled={loading}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            required
            label="City"
            value={billingDetails.address.city}
            onChange={(e) => {
              setBillingDetails({
                ...billingDetails,
                address: { ...billingDetails.address, city: e.target.value }
              });
              validateAddressField('city', e.target.value);
            }}
            error={!!addressErrors.city}
            helperText={addressErrors.city}
            disabled={loading}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth required error={!!addressErrors.state}>
            <InputLabel>State/Province</InputLabel>
            <Select
              value={billingDetails.address.state}
              onChange={(e) => {
                setBillingDetails({
                  ...billingDetails,
                  address: { ...billingDetails.address, state: e.target.value }
                });
                validateAddressField('state', e.target.value);
              }}
              label="State/Province"
              disabled={loading}
            >
              {getStatesForCountry(billingDetails.address.country)?.map(state => (
                <MenuItem key={state.code} value={state.code}>
                  {state.name}
                </MenuItem>
              )) || (
                <MenuItem value="">
                  <em>Enter state/province</em>
                </MenuItem>
              )}
            </Select>
            {addressErrors.state && (
              <FormHelperText>{addressErrors.state}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            required
            label={billingDetails.address.country === 'US' ? 'ZIP Code' : 'Postal Code'}
            value={billingDetails.address.postal_code}
            onChange={(e) => {
              setBillingDetails({
                ...billingDetails,
                address: { ...billingDetails.address, postal_code: e.target.value }
              });
              validateAddressField('postal_code', e.target.value);
            }}
            error={!!addressErrors.postal_code}
            helperText={addressErrors.postal_code}
            disabled={loading}
          />
        </Grid>

        <Grid item xs={12}>
          <FormControl fullWidth required error={!!addressErrors.country}>
            <InputLabel>Country</InputLabel>
            <Select
              value={billingDetails.address.country}
              onChange={(e) => {
                setBillingDetails({
                  ...billingDetails,
                  address: { 
                    ...billingDetails.address, 
                    country: e.target.value,
                    state: '' // Reset state when country changes
                  }
                });
                validateAddressField('country', e.target.value);
              }}
              label="Country"
              disabled={loading}
            >
              {SUPPORTED_COUNTRIES.map(country => (
                <MenuItem key={country.code} value={country.code}>
                  {country.name}
                </MenuItem>
              ))}
            </Select>
            {addressErrors.country && (
              <FormHelperText>{addressErrors.country}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        {/* Address Warnings */}
        {addressWarnings.length > 0 && (
          <Grid item xs={12}>
            <Alert severity="warning" style={{ marginBottom: 16 }}>
              {addressWarnings.map((warning, index) => (
                <Typography key={index} variant="body2">
                  {warning}
                </Typography>
              ))}
            </Alert>
          </Grid>
        )}

        {/* Promo Code */}
        <Grid item xs={12}>
          {!showPromoField ? (
            <Button
              startIcon={<PromoIcon />}
              onClick={() => setShowPromoField(true)}
              disabled={loading}
            >
              Have a promo code?
            </Button>
          ) : (
            <TextField
              fullWidth
              label="Promo Code"
              value={promoCode}
              onChange={(e) => {
                setPromoCode(e.target.value);
                setPromoValid(null);
              }}
              onBlur={validatePromoCode}
              disabled={loading}
              InputProps={{
                endAdornment: promoValid !== null && (
                  <InputAdornment position="end">
                    {promoValid ? (
                      <CheckCircleIcon color="success" />
                    ) : (
                      <Typography color="error" variant="caption">
                        Invalid code
                      </Typography>
                    )}
                  </InputAdornment>
                ),
              }}
            />
          )}
        </Grid>

        {/* Submit Button */}
        <Grid item xs={12}>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Processing...' : 'Continue to Payment'}
          </Button>
        </Grid>

        {/* Security Notice */}
        <Grid item xs={12}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <SecurityIcon fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary">
              Your payment information is encrypted and secure. You won't be charged during your free trial.
            </Typography>
          </div>
        </Grid>
      </Grid>
    </form>
  );
};

interface BillingSetupProps {
  organizationName?: string;
  organizationId?: string;
}

export const BillingSetup: React.FC<BillingSetupProps> = ({ 
  organizationName: propOrgName,
  organizationId: propOrgId 
}) => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [fetchedOrgName, setFetchedOrgName] = useState<string>('');

  // Get organization info from props, localStorage, or JWT token
  const getOrganizationFromToken = () => {
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return {
          organizationId: payload.organizationId || payload.organization_id,
          organizationName: payload.organizationName || payload.organization_name
        };
      } catch (e) {
        console.error('Failed to decode token:', e);
        return { organizationId: null, organizationName: null };
      }
    }
    return { organizationId: null, organizationName: null };
  };

  // Fetch organization name from API if needed
  const fetchOrganizationName = async (orgId: string): Promise<string> => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const response = await fetch(`${API_CONFIG.USER_SERVICE_URL}/api/v1/organizations/user-current`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data?.name) {
          console.log('[BillingSetup] Fetched organization name from API:', data.data.name);
          return data.data.name;
        }
      }
      console.warn('[BillingSetup] Failed to fetch organization name from API:', response.status);
      return '';
    } catch (error) {
      console.error('[BillingSetup] Error fetching organization name:', error);
      return '';
    }
  };

  const tokenOrgInfo = getOrganizationFromToken();
  
  // Debug logging to see what we have
  console.log('[BillingSetup] Organization info sources:', {
    propOrgName,
    propOrgId,
    pendingOrgName: localStorage.getItem('pendingOrganizationName'),
    pendingOrgId: localStorage.getItem('pendingOrganizationId'),
    tokenOrgInfo,
    allLocalStorage: {
      authToken: localStorage.getItem('authToken') ? 'present' : 'missing',
      token: localStorage.getItem('token') ? 'present' : 'missing',
      pendingOrganizationName: localStorage.getItem('pendingOrganizationName'),
      pendingOrganizationId: localStorage.getItem('pendingOrganizationId'),
    }
  });
  
  const localOrgName = propOrgName || localStorage.getItem('pendingOrganizationName') || tokenOrgInfo.organizationName;
  const organizationName = localOrgName || fetchedOrgName || '';
  // Store these values in state to preserve them even after localStorage is cleared
  const [subscriptionInfo] = useState(() => ({
    subscriptionTier: localStorage.getItem('pendingSubscriptionTier') || 'starter',
    billingFrequency: localStorage.getItem('pendingBillingFrequency') || 'monthly',
    selectedToolkit: localStorage.getItem('pendingSelectedToolkit') || null
  }));
  
  const organizationId = propOrgId || localStorage.getItem('pendingOrganizationId') || tokenOrgInfo.organizationId || '';
  const subscriptionTier = subscriptionInfo.subscriptionTier;
  const billingFrequency = subscriptionInfo.billingFrequency;
  const selectedToolkit = subscriptionInfo.selectedToolkit;
  
  // Debug logging for selectedToolkit
  console.log('[BillingSetup] Retrieved from localStorage:', {
    subscriptionTier,
    billingFrequency,
    selectedToolkit,
    rawSelectedToolkit: localStorage.getItem('pendingSelectedToolkit'),
    allLocalStorageKeys: Object.keys(localStorage),
  });

  useEffect(() => {
    const initializeBillingSetup = async () => {
      console.log('[BillingSetup] Organization info check:', {
        propOrgId,
        propOrgName,
        pendingOrgId: localStorage.getItem('pendingOrganizationId'),
        pendingOrgName: localStorage.getItem('pendingOrganizationName'),
        tokenOrgInfo,
        finalOrgId: organizationId,
        finalOrgName: organizationName,
        localOrgName,
        fetchedOrgName
      });
      
      if (!organizationId) {
        console.error('[BillingSetup] No organization ID found in any source');
        setError('No organization information found. Please complete signup first.');
        setLoading(false);
        return;
      }

      // If we have organization ID but no name, try to fetch it from API
      if (!localOrgName && !fetchedOrgName) {
        console.log('[BillingSetup] No organization name found locally, fetching from API...');
        try {
          const fetchedName = await fetchOrganizationName(organizationId);
          if (fetchedName) {
            setFetchedOrgName(fetchedName);
            console.log('[BillingSetup] Successfully fetched organization name:', fetchedName);
          } else {
            console.warn('[BillingSetup] Failed to fetch organization name, using default');
            setFetchedOrgName('Your Organization');
          }
        } catch (error) {
          console.error('[BillingSetup] Error fetching organization name:', error);
          setFetchedOrgName('Your Organization');
        }
      }
      
      console.log('[BillingSetup] Organization ID found, proceeding with billing setup');
      setLoading(false);
    };

    initializeBillingSetup();
  }, [organizationId, propOrgId, propOrgName, tokenOrgInfo, organizationName, localOrgName, fetchedOrgName]);

  const handleSuccess = async (customerId: string, subscriptionId: string) => {
    setSuccess(true);
    // Don't clean up localStorage here - let BillingComplete handle it
    // This way the subscription info is still available while showing "Finalizing..."
    
    // Poll for subscription creation with exponential backoff
    let attempts = 0;
    const maxAttempts = 10;
    const pollInterval = async () => {
      attempts++;
      
      try {
        console.log(`[BillingSetup] Polling attempt ${attempts}/${maxAttempts} for organizationId: ${organizationId}`);
        const response = await billingApi.getSubscription(organizationId);
        console.log('[BillingSetup] Subscription response:', response);
        
        // The response is already a Subscription object (billingApi.getSubscription extracts the data)
        const subscription = response;
        
        if (subscription && subscription.id && (subscription.status === 'active' || subscription.status === 'trialing')) {
          // Subscription created successfully
          console.log('[BillingSetup] Subscription found with valid status, navigating to chat');
          navigate('/chat');
          return;
        } else if (!subscription || !subscription.id) {
          // No subscription exists yet, continue polling
          console.log('[BillingSetup] No subscription found yet, continuing to poll');
        } else {
          console.log('[BillingSetup] Subscription found but status is:', subscription?.status);
        }
      } catch (error: any) {
        console.log('[BillingSetup] Error during subscription polling:', {
          status: error.response?.status,
          data: error.response?.data,
          message: error.message
        });
        
        // Any error at this point is unexpected
        console.log('[BillingSetup] Unexpected error during polling, navigating to chat anyway');
        navigate('/chat');
        return;
      }
      
      if (attempts < maxAttempts) {
        // Exponential backoff: 1s, 2s, 4s, 8s, etc...
        const delay = Math.min(1000 * Math.pow(2, attempts - 1), 10000);
        setTimeout(pollInterval, delay);
      } else {
        // Max attempts reached, navigate anyway
        // RequireSubscription component will handle the error
        navigate('/chat');
      }
    };
    
    // Start polling after initial delay for webhook processing
    setTimeout(pollInterval, 2000);
  };

  const handleError = (error: string) => {
    setError(error);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div style={{ height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ paddingTop: 24, paddingBottom: 24, paddingLeft: 32, paddingRight: 32, borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
        <Typography variant="h4" align="center">
          Complete Your Organization Setup
        </Typography>
        <Typography variant="body1" color="text.secondary" align="center" style={{ marginTop: 8 }}>
          Set up billing for {organizationName}
        </Typography>
      </div>

      {/* Main Content Area */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', justifyContent: 'center' }}>
        <div style={{ display: 'flex', width: '100%', maxWidth: 1400 }}>
          {/* Left Sidebar - Trial Information */}
          <div style={{
            width: 320,
            padding: 24,
            borderRight: '1px solid rgba(0,0,0,0.12)',
            backgroundColor: '#fff',
            overflowY: 'auto',
            flexShrink: 0
          }}>
            <Paper elevation={0} style={{ backgroundColor: '#e3f2fd', padding: 24 }}>
              <Typography variant="h6" gutterBottom color="primary">
                30-Day Free Trial Included
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Full access to all features" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="No charges during trial period" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Cancel anytime" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary={`$${PRICING[subscriptionTier as keyof typeof PRICING]?.[billingFrequency as 'monthly' | 'annual'] || 29}/month per user after trial`} />
                </ListItem>
              </List>
            </Paper>

            {/* Additional Info */}
            <div style={{ marginTop: 24 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                What's Included in Your {subscriptionTier === 'industry_specific' ? 'MEP Toolkit' : 
                  subscriptionTier.charAt(0).toUpperCase() + subscriptionTier.slice(1)} Plan:
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph component="div">
                {PLAN_FEATURES[subscriptionTier as keyof typeof PLAN_FEATURES]?.map((feature, index) => (
                  <span key={index} style={{ display: 'block' }}>
                    • {feature}
                  </span>
                )) || (
                  <>
                    • 5 custom AI teammates<br/>
                    • Basic knowledge graph<br/>
                    • Up to 5 team members<br/>
                    • 10GB storage<br/>
                    • Email support
                  </>
                )}
              </Typography>
            </div>

            <Divider style={{ marginTop: 24, marginBottom: 24 }} />

            <div>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Billing Information:
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Secure payment processing by Stripe<br/>
                • No setup fees<br/>
                • Monthly or annual billing<br/>
                • Cancel or change plan anytime
              </Typography>
            </div>
          </div>

          {/* Center Content - Billing Form */}
          <div style={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            overflowY: 'auto',
            padding: 32,
            minWidth: 0
          }}>
            <Card style={{ width: '100%', maxWidth: 650, margin: 'auto 0' }}>
            <CardContent style={{ padding: 32 }}>
              {error && (
                <Alert severity="error" style={{ marginBottom: 24 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {success ? (
                <div>
                  <Alert severity="success" style={{ marginBottom: 24 }}>
                    Payment method added successfully! Setting up your subscription...
                  </Alert>
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: 32, marginBottom: 32 }}>
                    <CircularProgress size={24} style={{ marginRight: 16 }} />
                    <Typography variant="body2" color="text.secondary">
                      Finalizing your subscription setup...
                    </Typography>
                  </div>
                </div>
              ) : (
                <BillingForm
                  organizationName={organizationName}
                  organizationId={organizationId}
                  subscriptionTier={subscriptionTier}
                  billingFrequency={billingFrequency}
                  selectedToolkit={selectedToolkit}
                  onSuccess={handleSuccess}
                  onError={handleError}
                />
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
    </div>
  );
};